<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import type { Map } from "mapbox-gl";
import { getSiteList, getSiteGeo } from "@carnet/api/backoffice";
import type { SiteResource } from "@carnet/api/backoffice";
import type { UrlFilterValues } from "@/composables/useUrlFilters";
import { formatBboxCoord } from "@carnet/ui/utils/geo";
import { locationFilterKey, apiRadiusKm } from "@carnet/ui/utils/location";
import { apiBool } from "@/utils/api";

definePageMeta({
  name: "sites",
  layout: "app",
});

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: "/sites",
  },
});

const { t } = useI18n();
const localePath = useLocalePath();
const { loggedIn } = useUserSession();
const { open: openSiteCreate } = useSiteCreateModal();

const { data: profile } = useProfileData();
const canFilterMySites = computed(() => loggedIn.value && (profile.value?.participationsCount ?? 0) > 0);

const { filter: locationFilter, clear: clearLocation, chip: locationChip } = useUrlLocationFilter({ withRadius: true });

const { multi } = useUrlFilterValues();
const activityTypeCodes = multi("activityTypes");
const zoneCodes = multi("zones");
const environmentCodes = multi("environments");
const localFeatureCodes = multi("localFeatures");
const depthCategoryCodes = multi("siteDepthCategories");
const situationCodes = multi("siteSituations");
const conditionCodes = multi("siteConditions");
const mySitesOnly = useMineFilter();

const filterCount = computed(
  () =>
    [activityTypeCodes, zoneCodes, environmentCodes, localFeatureCodes, depthCategoryCodes, situationCodes, conditionCodes].filter(
      (r) => r.value.length
    ).length +
    (mySitesOnly.value ? 1 : 0) +
    (locationFilter.value.location ? 1 : 0)
);

const { title, breadcrumbLabel } = usePageTitle({ base: () => t("sites.title"), filterCount });

useSeoMeta({
  title: () => title.value,
  description: () => t("seo.sites.description"),
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: breadcrumbLabel.value },
]);

const filterValues = ref<UrlFilterValues>({});

const mapRef = useTemplateRef<{ mapboxMap: Map }>("mapRef");
const layoutRef = useTemplateRef<{ sheetScrollEl: HTMLElement | null; closeSheet: () => void }>("layoutRef");
const isMobile = useIsMobile();

const { bbox, total, pageStates, firstPageStatus, totalPages, generation, registerContainer, setTopRef, loadPage, retry } =
  useGeoListSearch<SiteResource>({
    getMap: () => mapRef.value?.mapboxMap,
    getRoot: () => (isMobile.value ? (layoutRef.value?.sheetScrollEl ?? null) : null),
    resetWhen: [
      filterValues,
      activityTypeCodes,
      zoneCodes,
      environmentCodes,
      localFeatureCodes,
      depthCategoryCodes,
      situationCodes,
      conditionCodes,
      mySitesOnly,
      locationFilter,
    ],
    fetchPage: async ({ page, signal, bbox }) => {
      const res = await getSiteList({
        composable: "$fetch",
        signal,
        path: { locale: appLocale() },
        headers: { Accept: "application/json" },
        query: {
          page,
          sort: "center_asc",
          "bbox[nw][latitude]": formatBboxCoord(bbox.nwLat.value),
          "bbox[nw][longitude]": formatBboxCoord(bbox.nwLng.value),
          "bbox[se][latitude]": formatBboxCoord(bbox.seLat.value),
          "bbox[se][longitude]": formatBboxCoord(bbox.seLng.value),
          "activityTypeCodes[]": activityTypeCodes.value.length ? activityTypeCodes.value : undefined,
          "zoneCodes[]": zoneCodes.value.length ? zoneCodes.value : undefined,
          "environments[]": environmentCodes.value.length ? environmentCodes.value : undefined,
          "localFeatures[]": localFeatureCodes.value.length ? localFeatureCodes.value : undefined,
          "depthCategories[]": depthCategoryCodes.value.length ? depthCategoryCodes.value : undefined,
          "situations[]": situationCodes.value.length ? situationCodes.value : undefined,
          "conditions[]": conditionCodes.value.length ? conditionCodes.value : undefined,
          mineOnly: apiBool(mySitesOnly.value),
          "center[latitude]": locationFilter.value.center?.[1],
          "center[longitude]": locationFilter.value.center?.[0],
          radius: apiRadiusKm(locationFilter.value),
        },
      });
      return unwrapList<SiteResource>(res);
    },
  });

const GEO_CACHE_KEY = "sites-geo:default:v1";
const geoCacheKey = computed(() =>
  locationFilter.value.bbox ||
  activityTypeCodes.value.length ||
  zoneCodes.value.length ||
  environmentCodes.value.length ||
  localFeatureCodes.value.length ||
  depthCategoryCodes.value.length ||
  situationCodes.value.length ||
  conditionCodes.value.length ||
  mySitesOnly.value
    ? undefined
    : GEO_CACHE_KEY
);

const { data: geoData } = await getSiteGeo({
  composable: "useLazyFetch",
  key: "sites-geo",
  path: { locale: appLocale() },
  headers: { Accept: "application/json" },
  query: computed(() => ({
    "activityTypeCodes[]": activityTypeCodes.value.length ? activityTypeCodes.value : undefined,
    "zoneCodes[]": zoneCodes.value.length ? zoneCodes.value : undefined,
    "environments[]": environmentCodes.value.length ? environmentCodes.value : undefined,
    "localFeatures[]": localFeatureCodes.value.length ? localFeatureCodes.value : undefined,
    "depthCategories[]": depthCategoryCodes.value.length ? depthCategoryCodes.value : undefined,
    "situations[]": situationCodes.value.length ? situationCodes.value : undefined,
    "conditions[]": conditionCodes.value.length ? conditionCodes.value : undefined,
    mineOnly: apiBool(mySitesOnly.value),
    "center[latitude]": locationFilter.value.center?.[1],
    "center[longitude]": locationFilter.value.center?.[0],
    radius: apiRadiusKm(locationFilter.value),
  })),
});

const { geoMarkers } = useGeoSource({ data: geoData, cacheKey: geoCacheKey });


const { selectedId: selectedSiteId, onMarkerClick } = useMapSelection("siteId");

const locationKey = computed(() => locationFilterKey(locationFilter.value));
useAppDeselectOnChange(selectedSiteId, [
  activityTypeCodes,
  zoneCodes,
  environmentCodes,
  localFeatureCodes,
  depthCategoryCodes,
  situationCodes,
  conditionCodes,
  mySitesOnly,
  locationKey,
]);

useMapInitialFocus(() => mapRef.value?.mapboxMap, geoMarkers, selectedSiteId.value, { zoom: 11 });

const { focusOnLocation } = useMapLocationFocus(() => mapRef.value?.mapboxMap, locationFilter);

function onMapInit() {
  bbox.update();
  focusOnLocation(false);
}

// « Créer un site » : rouvre le tunnel sur le cadrage courant de la carte (centre + zoom) plutôt
// que sur le monde entier.
function onCreateSite() {
  const map = mapRef.value?.mapboxMap;
  const view = map ? { center: [map.getCenter().lng, map.getCenter().lat] as [number, number], zoom: map.getZoom() } : undefined;
  openSiteCreate(view);
}

function onShowSiteOnMap(site: SiteResource) {
  selectedSiteId.value = String(site.id);
  layoutRef.value?.closeSheet();
}

async function onSelectSite(site: SiteResource) {
  // Retirer la zone AVANT de sélectionner : sur /sites, filtre et sélection vivent dans l'URL ;
  // séquencer évite la course de navigation et la désélection auto (useAppDeselectOnChange).
  if (locationFilter.value.location) {
    await clearLocation();
    await nextTick();
  }
  selectedSiteId.value = String(site.id);
  const map = mapRef.value?.mapboxMap;
  if (!map) return;
  const feature = geoMarkers.value.features.find((f) => f.properties?.id === site.id);
  if (feature && feature.geometry.type === "Point") {
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    map.flyTo({ center: [lng, lat], zoom: 11, animate: true, duration: 800 });
  } else if (site.latitude != null && site.longitude != null && site.latitude !== 0 && site.longitude !== 0) {
    map.flyTo({ center: [site.longitude, site.latitude], zoom: 11, animate: true, duration: 800 });
  }
}
</script>

<template>
  <WebMapListPage type="sites" ref="layoutRef" :breadcrumb="breadcrumb" :total="total" :bottom-padding="16" class="grow">
    <template #search>
      <WebLocationSearch
        v-model:filter="locationFilter"
        context="sites"
        with-radius
        :placeholder="t('sites.search.placeholder')"
        class="mb-4"
        @select:site="onSelectSite"
      />
      <WebFiltersSites v-model:applied="filterValues" :search-chip="locationChip" @clear:search="clearLocation" />
    </template>

    <template #aside>
      <WebInfiniteList
        :page-states="pageStates"
        :first-page-status="firstPageStatus"
        :total="total"
        :total-pages="totalPages"
        :generation="generation"
        :register-container="registerContainer"
        :set-top-ref="setTopRef"
        @load="loadPage"
        @retry="retry"
      >
        <template #count>{{ t("sites.count", total) }}</template>
        <template #skeleton>
          <AppTileSiteSkeleton class="py-6 px-4 sm:px-10" />
        </template>
        <template #default="{ item }">
          <AppTileSite :site="item" opens-on-map class="py-6 px-4 sm:px-10" @select="() => onShowSiteOnMap(item)" />
        </template>
        <template #footer>
          <div v-if="loggedIn" class="pt-6 pb-10 px-10">
            <p class="mb-4">{{ t("sites.create.prompt") }}</p>
            <UButton variant="link" trailing-icon="icon:arrowRight" @click="onCreateSite">
              {{ t("sites.create.link") }}
            </UButton>
          </div>
        </template>
      </WebInfiniteList>
    </template>

    <template #map="{ isMobile: isMapMobile, openSheet, triggerLabel, bottomPadding: mapBottomPadding }">
      <ClientOnly>
        <LazyAppMap
          ref="mapRef"
          :markers="geoMarkers"
          :active-feature-id="selectedSiteId"
          :controls-dimmed="isMapMobile && selectedSiteId != null"
          mode="cluster"
          class="size-full"
          @init="onMapInit"
          @move-end="bbox.update"
          @marker-click="onMarkerClick"
          :bottom-padding="mapBottomPadding"
        >
          <WebMapPopupSite v-if="selectedSiteId && !isMapMobile" :site-id="selectedSiteId" @close="selectedSiteId = undefined" />
          <template #bottomLeft>
            <UButton v-if="canFilterMySites" as="label" color="neutral" variant="soft" class="cursor-pointer">
              <input v-model="mySitesOnly" type="checkbox" name="mineOnly" class="size-4 rounded-xs accent-primary cursor-pointer" />
              {{ $t("sites.filters.mySites") }}
            </UButton>
          </template>
          <template v-if="isMapMobile" #bottom>
            <AppMobileFloatingMessage v-if="firstPageStatus === 'success' && total === 0">
              {{ t("ui.zone.empty")
              }}<template v-if="loggedIn"
                >, <UButton variant="link" @click="onCreateSite">{{ t("sites.create.link.short") }}</UButton></template
              >
            </AppMobileFloatingMessage>
            <AppMobileFloatingButton v-else :label="triggerLabel ?? ''" icon="icon:chevronDown" @click="openSheet" />
          </template>
          <template v-if="isMapMobile" #pinned>
            <WebMapPopupSite v-if="selectedSiteId" :site-id="selectedSiteId" @close="selectedSiteId = undefined" />
          </template>
        </LazyAppMap>
      </ClientOnly>
    </template>
  </WebMapListPage>
</template>
